import { SEARCH_MARK_END, SEARCH_MARK_START, type SearchHit } from '@kanto/shared';
import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { errorText } from '../api/client';
import { useSearch } from '../api/hooks';
import { formatDue, formatRemindAt } from '../lib/dates';
import { useT, type MessageKey } from '../lib/i18n';
import { TagChips } from './TagChips';

/**
 * The layer that finds anything, from anywhere.
 *
 * **Grouped by module, in the order of the rail** — todos, reminders, notes —
 * and each group capped, so a hundred matching notes cannot bury the one todo
 * that also matched. The order inside a group is the server's ranking, which the
 * front end does not re-sort: two rankings would eventually disagree about which
 * is better.
 *
 * The four states are the ones a field being typed into actually passes through.
 * Empty is not blank — it is what was touched last, which is the most likely
 * thing to be looking for. **In flight, the previous results stay and go grey**
 * rather than disappearing: a list that empties on every letter blinks, and a
 * blinking list cannot be read while typing. Nothing found says so. Offline says
 * why, since there is no index in the browser to fall back on.
 */

/** How long the typing has to stop before a request goes out. */
const DEBOUNCE_MS = 150;

/** How many hits one module may show. Enough to recognise, not enough to bury. */
const PER_GROUP = 5;

/** The rail's order, and therefore this list's. */
const GROUPS: { kind: SearchHit['kind']; label: MessageKey }[] = [
  { kind: 'todo', label: 'module.todos' },
  { kind: 'reminder', label: 'module.reminders' },
  { kind: 'note', label: 'module.notes' },
];

export function SearchPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement | null {
  const t = useT();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);

  // The request follows the typing rather than each keystroke. 150 ms is under
  // the pause between two letters and over the time a key repeat takes.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebounced('');
      setActive(0);
    }
  }, [open]);

  const { data, error, isFetching } = useSearch(debounced, open);

  const hits = data?.hits ?? [];
  // Grouped for drawing, flattened for the keyboard: the arrows walk one list
  // whatever the headings suggest, which is what everybody expects of them.
  const shown = GROUPS.flatMap(({ kind }) =>
    hits.filter((hit) => hit.kind === kind).slice(0, PER_GROUP),
  );
  const highlighted = Math.min(active, Math.max(shown.length - 1, 0));

  const go = (hit: SearchHit): void => {
    onClose();
    // A note has a screen of its own. A todo and a reminder do not, so the list
    // is opened with the row named — otherwise a hit would land somebody on a
    // list of forty rows with no idea which one they had just chosen.
    if (hit.kind === 'note') void navigate(`/notes/${hit.id}`);
    else void navigate(`/${hit.kind}s?highlight=${encodeURIComponent(hit.id)}`);
  };

  if (!open) return null;

  const offline = !navigator.onLine;

  return (
    <div
      onMouseDown={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink-900/20 px-5 pt-20 backdrop-blur-sm"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('search.open')}
        onMouseDown={(event) => event.stopPropagation()}
        className="flex w-full max-w-xl flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-lg"
      >
        <div className="flex items-center gap-2 border-b border-line px-4">
          <svg
            viewBox="0 0 24 24"
            className="size-5 shrink-0 text-ink-400"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="6" />
            <path d="m20 20-4.5-4.5" />
          </svg>
          <input
            type="text"
            value={query}
            autoFocus
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') onClose();
              if (event.key === 'ArrowDown' && shown.length > 0) {
                event.preventDefault();
                setActive((index) => (index + 1) % shown.length);
              }
              if (event.key === 'ArrowUp' && shown.length > 0) {
                event.preventDefault();
                setActive((index) => (index <= 0 ? shown.length - 1 : index - 1));
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                const hit = shown[highlighted];
                if (hit) go(hit);
              }
            }}
            placeholder={t('search.placeholder')}
            aria-label={t('search.open')}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="min-h-12 w-full bg-transparent text-base outline-none placeholder:text-ink-400"
          />
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {offline && <Notice>{t('search.offline')}</Notice>}

          {!offline && error !== null && <Notice>{errorText(error, t('search.failed'))}</Notice>}

          {!offline && error === null && shown.length === 0 && (
            <Notice>{debounced.trim() === '' ? t('search.empty') : t('search.nothing')}</Notice>
          )}

          {shown.length > 0 && (
            // Greyed while a request is out, rather than cleared: what is on
            // screen is the previous answer, and saying so is more useful than
            // showing nothing.
            <div className={isFetching ? 'opacity-60 transition-opacity' : ''}>
              {GROUPS.map(({ kind, label }) => {
                const group = hits.filter((hit) => hit.kind === kind).slice(0, PER_GROUP);
                if (group.length === 0) return null;

                return (
                  <section key={kind}>
                    <h2 className="px-4 pt-3 pb-1 text-xs font-semibold tracking-wide text-ink-400 uppercase">
                      {t(label)}
                    </h2>
                    <ul>
                      {group.map((hit) => (
                        <li key={`${hit.kind}-${hit.id}`}>
                          <button
                            type="button"
                            // Before the click, so the field keeps the focus and
                            // the arrows still work after a pointer has been
                            // near the list.
                            onMouseDown={(event) => event.preventDefault()}
                            onMouseEnter={() => setActive(shown.indexOf(hit))}
                            onClick={() => go(hit)}
                            className={`flex w-full items-start gap-3 px-4 py-2.5 text-left ${
                              shown[highlighted] === hit ? 'bg-accent-soft' : ''
                            }`}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-ink-900">
                                {hit.title}
                              </span>
                              {hit.excerpt !== null && (
                                <span className="mt-0.5 block truncate text-xs text-ink-500">
                                  <Excerpt text={hit.excerpt} />
                                </span>
                              )}
                              <TagChips tags={hit.tags} />
                            </span>
                            <Aside hit={hit} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>

        <p className="border-t border-line px-4 py-2 text-xs text-ink-400">{t('search.hint')}</p>
      </div>
    </div>
  );
}

/** What each module puts on the right of its row, and nothing it does not have. */
function Aside({ hit }: { hit: SearchHit }): ReactElement | null {
  const t = useT();

  if (hit.kind === 'todo') {
    return hit.dueOn === null ? null : (
      <span className="mt-0.5 shrink-0 text-xs tabular-nums text-ink-400">
        {formatDue(hit.dueOn, t)}
      </span>
    );
  }
  if (hit.kind === 'reminder') {
    return (
      <span className="mt-0.5 shrink-0 text-xs tabular-nums text-ink-400">
        {formatRemindAt(hit.remindAt, t)}
      </span>
    );
  }
  return null;
}

/**
 * The excerpt, with the matched run marked.
 *
 * The server sends two control characters around it rather than `<mark>`, so
 * this is where the element is made — an excerpt is text somebody wrote, and
 * markup arriving from the API would be the one string in the application a
 * browser has to interpret.
 */
function Excerpt({ text }: { text: string }): ReactElement {
  const pieces: { text: string; marked: boolean }[] = [];
  let rest = text;

  while (rest.length > 0) {
    const start = rest.indexOf(SEARCH_MARK_START);
    if (start === -1) {
      pieces.push({ text: rest, marked: false });
      break;
    }
    if (start > 0) pieces.push({ text: rest.slice(0, start), marked: false });

    const after = rest.slice(start + SEARCH_MARK_START.length);
    const end = after.indexOf(SEARCH_MARK_END);
    if (end === -1) {
      pieces.push({ text: after, marked: true });
      break;
    }
    pieces.push({ text: after.slice(0, end), marked: true });
    rest = after.slice(end + SEARCH_MARK_END.length);
  }

  return (
    <>
      {pieces.map((piece, index) =>
        piece.marked ? (
          <mark key={index} className="bg-accent-soft font-medium text-accent">
            {piece.text}
          </mark>
        ) : (
          <span key={index}>{piece.text}</span>
        ),
      )}
    </>
  );
}

function Notice({ children }: { children: string }): ReactElement {
  return <p className="px-4 py-6 text-center text-sm text-ink-400">{children}</p>;
}
